/* Home.
 *
 * THE CODE COMES FIRST, which is the one place this screen leaves the artboard's
 * order. The artboard opens Home on a four-step "secure your account" card.
 * Two of its steps do not exist (a recovery phrase, an authenticator key), and
 * the reason a customer opens this app is to show a code to someone serving
 * them. So the live code is the first thing on the first screen, and nothing
 * above it can push it down.
 *
 * Then, in the artboard's order: a reward ready to claim, the setup steps that
 * are real, the cards closest to a reward, and what happened most recently.
 * The artboard's summary tiles keep their place under the code, with numbers
 * the API can stand behind.
 */
import { type StoredSecret, rankCards, t } from "@damga/kit";

import type { Card } from "../App";
import { CodeDigits } from "../ui";
import { ActivityRow, type Entry } from "./Activity";
import { CardRow } from "./Wallet";

export interface SetupState {
  hasName: boolean;
  canPasskey: boolean;
  hasPasskey: boolean;
  hasEmail: boolean;
  /** Whether to ask for an email at all yet. */
  askEmail: boolean;
}

export function Home({
  secret,
  cards,
  entries,
  setup,
  favourites,
  onToggleFavourite,
  onOpenCard,
  onGoWallet,
  onGoActivity,
  onAddName,
  onUsePasskey,
  onAddEmail,
}: {
  secret: StoredSecret | null;
  cards: Card[];
  entries: Entry[] | null;
  setup: SetupState;
  favourites: string[];
  onToggleFavourite: (card: Card) => void;
  onOpenCard: (id: string) => void;
  onGoWallet: () => void;
  onGoActivity: () => void;
  onAddName: () => void;
  onUsePasskey: () => void;
  onAddEmail: () => void;
}) {
  const live = cards.filter((c) => !c.archived_at);
  const ranked = rankCards(live);
  const due = ranked.find((c) => c.claimable > 0);
  const ready = live.reduce((n, c) => n + c.claimable, 0);
  // Only when every card carries it: an API older than the field would make a
  // sum of some cards look like a total.
  const visits = live.every((c) => typeof c.visits === "number")
    ? live.reduce((n, c) => n + (c.visits ?? 0), 0)
    : null;

  return (
    <>
      {/* TWO HALVES ON A WIDE SCREEN (review finding 25, 2026-09-19): the code and
          its facts, which stay in view, and what there is to do. On a phone both
          wrappers are display: contents and the page is the single column it was. */}
      <div className="home-code">
      <section className="code-panel" aria-labelledby="home-code">
        <div className="section-head">
          <h2 className="label" data-accent="" id="home-code">
            {t("code.title")}
          </h2>
        </div>
        <CodeDigits secret={secret} />
        <p className="notice">{t("code.works_offline")}</p>
      </section>

      {live.length > 0 && (
        <div className="stats" data-count={visits === null ? 2 : 3}>
          <Stat v={live.length} k={t("home.stat.cards")} />
          <Stat v={ready} k={t("home.stat.ready")} accent />
          {visits !== null && <Stat v={visits} k={t("home.stat.visits")} />}
        </div>
      )}
      </div>

      <div className="home-rest">
      {due && (
        <button type="button" className="due" onClick={() => onOpenCard(due.membership_id)}>
          <span>
            <span className="label">{t("wallet.readyToClaim")}</span>
            <span className="due-reward">
              {due.reward_description}
            </span>
            <span className="due-shop">{due.business_name}</span>
          </span>
          <span className="due-arrow" aria-hidden="true">
            →
          </span>
        </button>
      )}

      <Setup
        setup={setup}
        onAddName={onAddName}
        onUsePasskey={onUsePasskey}
        onAddEmail={onAddEmail}
      />

      {live.length === 0 ? (
        <div className="empty">
          <p className="empty-title">{t("wallet.empty.title")}</p>
          <p className="empty-body">{t("wallet.empty.body")}</p>
        </div>
      ) : (
        <section>
          <div className="section-head">
            <h2 className="label">{t("home.closest")}</h2>
            <button type="button" className="link-mono" onClick={onGoWallet}>
              {t("home.allCards")}
            </button>
          </div>
          <ul className="cards">
            {ranked.slice(0, 2).map((c) => (
              <li key={c.membership_id}>
                <CardRow
                  card={c}
                  favourite={favourites.includes(c.membership_id)}
                  onToggleFavourite={() => onToggleFavourite(c)}
                  onOpen={() => onOpenCard(c.membership_id)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {entries && entries.length > 0 && (
        <section>
          <div className="section-head">
            <h2 className="label">{t("home.recent")}</h2>
            <button type="button" className="link-mono" onClick={onGoActivity}>
              {t("home.allActivity")}
            </button>
          </div>
          <ol className="rows">
            {entries.slice(0, 3).map((e) => (
              <ActivityRow key={e.entry_id} entry={e} brief />
            ))}
          </ol>
        </section>
      )}
      </div>
    </>
  );
}

function Stat({ v, k, accent = false }: { v: number; k: string; accent?: boolean }) {
  return (
    <div className="stat">
      <p className="stat-v" data-accent={accent ? "" : undefined}>
        {v.toLocaleString("en-GB")}
      </p>
      <p className="stat-k">{k}</p>
    </div>
  );
}

/* THE SETUP CARD, WITH ONLY THE STEPS THAT EXIST.
 *
 * Name, passkey and email. The passkey appears only where this device can make
 * one; the email only once the stamps justify asking (the earlier ruling, kept),
 * or once one is on file. Done steps stay, ticked, so the count means something;
 * the card goes when every step it shows is done. */
function Setup({
  setup,
  onAddName,
  onUsePasskey,
  onAddEmail,
}: {
  setup: SetupState;
  onAddName: () => void;
  onUsePasskey: () => void;
  onAddEmail: () => void;
}) {
  const steps = [
    { key: "name", done: setup.hasName, title: t("profile.name.title"), body: t("profile.name.body"), go: onAddName },
    ...(setup.canPasskey
      ? [{ key: "passkey", done: setup.hasPasskey, title: t("profile.passkey.title"), body: t("profile.passkey.body"), go: onUsePasskey }]
      : []),
    ...(setup.askEmail || setup.hasEmail
      ? [{ key: "email", done: setup.hasEmail, title: t("profile.email.title"), body: t("profile.email.body"), go: onAddEmail }]
      : []),
  ];
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  return (
    <section className="onb" aria-labelledby="setup-title">
      <div className="onb-head">
        <h2 className="label" data-accent="" id="setup-title">
          {t("profile.title")}
        </h2>
        <span className="onb-count">
          {done} / {steps.length}
        </span>
      </div>
      <div className="onb-track" aria-hidden="true">
        <div className="onb-fill" style={{ ["--pct" as string]: String(done / steps.length) }} />
      </div>
      <ol className="onb-steps">
        {steps.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              className="onb-step"
              data-done={s.done ? "" : undefined}
              onClick={s.done ? undefined : s.go}
              aria-disabled={s.done || undefined}
            >
              <span className="onb-badge" aria-hidden="true">
                {s.done ? "✓" : i + 1}
              </span>
              <span className="shrink">
                <span className="onb-title">
                  {s.title}
                </span>
                <span className="onb-body">{s.body}</span>
              </span>
              <span className="onb-tag">{s.done ? t("home.step.done") : t("settings.setUp")}</span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
